import { useEffect, useMemo, useRef, useState } from 'react'
import { api, ApiError } from '../../api/client'
import type {
  CentralCampaign,
  Item,
  ItemFamily,
  ItemPurchaseInfo,
  ItemSubFamily,
  SpecialOrder,
  SpecialOrderAvailability,
  SpecialOrderStatus,
} from '../../api/types'
import { useStores } from '../../app/StoreContext'
import { useMe } from '../../app/useMe'
import {
  Badge,
  Button,
  Card,
  Empty,
  ErrorBox,
  Field,
  Num,
  Spinner,
  formatDate,
  formatDateTime, plural } from '../../ui/primitives'
import { GridHead, GridSummary, useGrid, type ColumnDef } from '../../ui/grid'
import { SupplierPicker } from '../../ui/SupplierPicker'
import { CampaignEditor } from './CampaignEditor'

/**
 * Ordini speciali: quello che il punto vendita chiede fuori dal riordino ordinario, più le
 * campagne che la sede distribuisce sulla rete.
 *
 * La parte che fa risparmiare telefonate è la creazione: prima di inviare si vedono i dati
 * d'acquisto dell'articolo per quel punto vendita — confezione e soprattutto se è ordinabile —
 * e il multiplo di confezione che verrà effettivamente registrato. Il costo del prodotto non
 * viene mostrato: non esce dall'API.
 */

const STATUS_FILTERS: { key: SpecialOrderStatus | 'all'; label: string }[] = [
  { key: 'Pending', label: 'In sospeso' },
  { key: 'Ordered', label: 'Ordinati' },
  { key: 'Cancelled', label: 'Annullati' },
  { key: 'all', label: 'Tutti' },
]

/**
 * Arrotondamento al multiplo di confezione, identico alla regola applicata dal server
 * (`PackRounding.ToNearestPack`, trascritta dal legacy). Il punto delicato è la metà esatta:
 * un resto pari a mezza confezione resta GIÙ — 25 pezzi con confezione da 10 sono 20, non 30.
 * Con `Math.round` l'anteprima annunciava una confezione in più di quella che il server
 * avrebbe registrato. Serve solo per anticipare a video: il numero buono resta quello di ritorno.
 */
function toNearestPack(requested: number, packSize: number) {
  if (packSize <= 1) return Math.max(requested, 0)
  if (requested <= 0) return 0
  const remainder = requested % packSize
  let quantity = requested - remainder
  if (remainder > 0 && (remainder > packSize / 2 || quantity === 0)) quantity += packSize
  return quantity
}

export function SpecialOrdersPage() {
  const { selected, storeFilter, storesReady, stores, storeName } = useStores()
  const { hasPermission } = useMe()
  const canManageCampaigns = hasPermission('specialOrders.central')
  const storeKey = selected.join(',')

  const [status, setStatus] = useState<SpecialOrderStatus | 'all'>('Pending')
  const [orders, setOrders] = useState<SpecialOrder[]>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<unknown>()
  const [reloadToken, setReloadToken] = useState(0)

  const [creating, setCreating] = useState(false)
  const [createdNote, setCreatedNote] = useState<string>()

  // Annullamento: la motivazione si scrive nella riga stessa, così resta chiaro cosa si sta annullando.
  const [cancelTarget, setCancelTarget] = useState<number>()
  const [cancelReason, setCancelReason] = useState('')
  const [cancelBusy, setCancelBusy] = useState(false)
  const [cancelProblem, setCancelProblem] = useState<Record<number, string>>({})

  // Modifica della quantità in linea, senza riaprire la riga.
  const [editTarget, setEditTarget] = useState<number>()
  const [editValue, setEditValue] = useState('')
  const [editBusy, setEditBusy] = useState(false)

  useEffect(() => {
    let cancelled = false
    // Con l'elenco dei punti vendita ancora in arrivo il filtro non è affidabile: si aspetta.
    if (!storesReady) return
    setLoading(true)
    setError(undefined)

    api
      .get<SpecialOrder[]>('/special-orders', {
        storeCode: storeFilter,
        status: status === 'all' ? undefined : status,
      })
      .then((data) => {
        if (!cancelled) setOrders(data)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setError(e)
        setOrders(undefined)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeKey, storesReady, status, reloadToken])

  const reload = () => setReloadToken((t) => t + 1)

  const cancelOrder = async (order: SpecialOrder) => {
    setCancelBusy(true)
    setCancelProblem((previous) => {
      const next = { ...previous }
      delete next[order.id]
      return next
    })

    try {
      await api.del<void>(`/special-orders/${order.id}`, { reason: cancelReason.trim() || undefined })
      setCancelTarget(undefined)
      setCancelReason('')
      reload()
    } catch (e) {
      const message =
        e instanceof ApiError ? e.detail || e.title : 'Annullamento non riuscito. Riprova fra qualche istante.'
      setCancelProblem((previous) => ({ ...previous, [order.id]: message }))
    } finally {
      setCancelBusy(false)
    }
  }

  const updateQuantity = async (order: SpecialOrder) => {
    const qty = Math.trunc(Number(editValue))
    if (!Number.isFinite(qty) || qty < 1) {
      setCancelProblem((previous) => ({ ...previous, [order.id]: 'La quantità deve essere almeno 1.' }))
      return
    }

    setEditBusy(true)
    setCancelProblem((previous) => {
      const next = { ...previous }
      delete next[order.id]
      return next
    })

    try {
      const updated = await api.patch<SpecialOrder>(`/special-orders/${order.id}`, { quantity: qty })
      setEditTarget(undefined)
      // Il valore buono è quello di ritorno: il server arrotonda alla confezione.
      setOrders((current) => current?.map((o) => (o.id === updated.id ? updated : o)))
      if (updated.quantity !== qty)
        setCancelProblem((previous) => ({
          ...previous,
          [order.id]: `Richiesti ${qty} pezzi, registrati ${updated.quantity} per l’arrotondamento alla confezione.`,
        }))
    } catch (e) {
      const message =
        e instanceof ApiError ? e.detail || e.title : 'Modifica non riuscita. Riprova fra qualche istante.'
      setCancelProblem((previous) => ({ ...previous, [order.id]: message }))
    } finally {
      setEditBusy(false)
    }
  }

  /** Le righe nate da una campagna di sede sono in sola lettura per chi non è la sede. */
  const canTouch = (order: SpecialOrder) => order.origin === 'Store' || canManageCampaigns

  const orderColumns = useMemo<ColumnDef<SpecialOrder>[]>(
    () => [
      { key: 'store', label: 'Punto vendita', value: (o) => `${o.store} ${storeName(o.store) ?? ''}` },
      { key: 'item', label: 'Articolo', value: (o) => o.item },
      { key: 'description', label: 'Descrizione', value: (o) => o.itemDescription },
      { key: 'supplier', label: 'Fornitore', value: (o) => o.supplier },
      { key: 'quantity', label: 'Quantità', value: (o) => o.quantity, numeric: true },
      {
        key: 'origin',
        label: 'Origine',
        value: (o) => (o.origin === 'Central' ? 'campagna di sede' : 'negozio'),
      },
      { key: 'requestedBy', label: 'Richiesto da', value: (o) => o.requestedBy },
      { key: 'requestedAt', label: 'Data', value: (o) => o.requestedAt },
      {
        key: 'status',
        label: 'Stato',
        value: (o) =>
          o.status === 'Pending' ? 'in sospeso'
          : o.status === 'Ordered' ? `ordinato ${o.orderNumber ?? ''}`
          : 'annullato',
      },
    ],
    [storeName],
  )
  const grid = useGrid(orders ?? [], orderColumns)

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Ordini speciali</h1>
          <p className="muted" style={{ margin: '4px 0 0' }}>
            {selected.length === 0 ?
              'Tutti i punti vendita visibili'
            : `${plural(selected.length, 'punto vendita', 'punti vendita')} selezionat${selected.length === 1 ? 'o' : 'i'}`}
          </p>
        </div>

        <div className="page-tools">
          {STATUS_FILTERS.map((f) => (
            <Button key={f.key} variant={status === f.key ? 'primary' : 'default'} onClick={() => setStatus(f.key)}>
              {f.label}
            </Button>
          ))}
          <Button onClick={reload} disabled={loading}>
            Aggiorna
          </Button>
          <Button variant={creating ? 'ghost' : 'primary'} onClick={() => setCreating((c) => !c)}>
            {creating ? 'Chiudi' : 'Nuovo ordine speciale'}
          </Button>
        </div>
      </div>

      {createdNote && (
        <div className="notice notice-ok">
          <strong>Ordine speciale registrato</strong>
          <p>{createdNote}</p>
        </div>
      )}

      {creating && (
        <NewSpecialOrder
          stores={stores.map((s) => ({ code: s.code, name: s.name }))}
          defaultStore={selected[0] ?? ''}
          onCreated={(order, requested, summedFrom) => {
            setCreatedNote(
              `${order.itemDescription} (${order.item}) per il punto vendita ${order.store}: ` +
                (summedFrom !== undefined ?
                  `${requested} pezzi aggiunti ai ${summedFrom} già richiesti — la richiesta è ora di ${order.quantity} pezzi.`
                : order.quantity === requested ?
                  `${order.quantity} pezzi.`
                : `richiesti ${requested} pezzi, registrati ${order.quantity} per l’arrotondamento alla confezione.`),
            )
            setCreating(false)
            reload()
          }}
        />
      )}

      <Card title="Elenco">
        {loading && <Spinner />}

        {!loading && error !== undefined && <ErrorBox error={error} onRetry={reload} />}

        {!loading && error === undefined && orders && orders.length === 0 && (
          <Empty>
            Nessun ordine speciale
            {status === 'all' ? '' : ` nello stato “${STATUS_FILTERS.find((f) => f.key === status)?.label}”`}
            {selected.length === 0 ? ' nei punti vendita visibili' : ' nei punti vendita selezionati'}. Gli
            ordini speciali nascono da una richiesta del negozio o da una campagna di sede: se ne attendi
            uno, verifica il punto vendita selezionato in alto o cambia stato.
          </Empty>
        )}

        {!loading && error === undefined && orders && orders.length > 0 && (
          <div className="table-wrap">
            <table>
              <GridHead columns={orderColumns} grid={grid} extraColumns={1} />
              <tbody>
                {grid.rows.map((order) => (
                  <tr key={order.id}>
                    <td>
                      <div className="store-code">{order.store}</div>
                      <div className="muted">{storeName(order.store)}</div>
                    </td>
                    <td className="num">{order.item}</td>
                    <td>
                      {order.itemDescription}
                      {order.note && <div className="muted">{order.note}</div>}
                      {cancelProblem[order.id] && <div className="cell-problem">{cancelProblem[order.id]}</div>}
                      {cancelTarget === order.id && (
                        <div className="inline" style={{ marginTop: 6 }}>
                          <input
                            style={{ maxWidth: 260 }}
                            placeholder="Motivo dell’annullamento"
                            value={cancelReason}
                            onChange={(e) => setCancelReason(e.target.value)}
                            autoFocus
                          />
                          <Button variant="danger" disabled={cancelBusy} onClick={() => void cancelOrder(order)}>
                            {cancelBusy ? 'Annullamento…' : 'Conferma annullamento'}
                          </Button>
                          <Button
                            variant="ghost"
                            disabled={cancelBusy}
                            onClick={() => {
                              setCancelTarget(undefined)
                              setCancelReason('')
                            }}
                          >
                            Lascia stare
                          </Button>
                        </div>
                      )}
                    </td>
                    <td className="num">{order.supplier}</td>
                    <td className="num">
                      {editTarget === order.id ?
                        <span className="inline" style={{ justifyContent: 'flex-end' }}>
                          <input
                            className="qty-input"
                            style={{ maxWidth: 80 }}
                            inputMode="numeric"
                            value={editValue}
                            autoFocus
                            disabled={editBusy}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') void updateQuantity(order)
                              if (e.key === 'Escape') setEditTarget(undefined)
                            }}
                          />
                          <Button disabled={editBusy} onClick={() => void updateQuantity(order)}>
                            {editBusy ? '…' : 'Salva'}
                          </Button>
                          <Button variant="ghost" disabled={editBusy} onClick={() => setEditTarget(undefined)}>
                            ×
                          </Button>
                        </span>
                      : <>
                          <Num value={order.quantity} />
                          {order.status === 'Pending' && canTouch(order) && (
                            <button
                              type="button"
                              className="row-edit"
                              title="Modifica la quantità"
                              onClick={() => {
                                setEditTarget(order.id)
                                setEditValue(String(order.quantity))
                              }}
                            >
                              ✎
                            </button>
                          )}
                        </>
                      }
                    </td>
                    <td
                      title={
                        order.origin === 'Central' ?
                          'Generato da una campagna di sede: quantità e annullamento li gestisce la sede.'
                        : 'Richiesto dal punto vendita'
                      }
                    >
                      <Badge tone={order.origin === 'Central' ? 'info' : 'neutral'}>
                        {order.origin === 'Central' ? 'Da campagna di sede' : 'Richiesto dal negozio'}
                      </Badge>
                    </td>
                    <td>{order.requestedBy}</td>
                    <td>{formatDateTime(order.requestedAt)}</td>
                    <td>
                      <Badge
                        tone={
                          order.status === 'Pending' ? 'warn'
                          : order.status === 'Ordered' ? 'ok'
                          : 'muted'
                        }
                      >
                        {order.status === 'Pending' ?
                          'In sospeso'
                        : order.status === 'Ordered' ?
                          `Ordinato${order.orderNumber ? ` · ${order.orderNumber}` : ''}`
                        : 'Annullato'}
                      </Badge>
                    </td>
                    <td>
                      {order.status === 'Pending' && cancelTarget !== order.id && canTouch(order) && (
                        <Button
                          variant="ghost"
                          onClick={() => {
                            setCancelTarget(order.id)
                            setCancelReason('')
                          }}
                        >
                          Annulla
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <GridSummary grid={grid} total={orders.length} />
          </div>
        )}
      </Card>

      {/* Le campagne sono della sede: creazione e gestione richiedono specialOrders.central
          (responsabile logistico o admin). Il server rifiuta comunque; qui si evita di mostrare
          una sezione che risponderebbe «non autorizzato». */}
      {canManageCampaigns && <Campaigns />}
    </>
  )
}

// --- Nuovo ordine speciale ----------------------------------------------------------------------

/** Criteri di ricerca articolo: tutti facoltativi, si combinano fra loro. */
interface ItemFilters {
  familyCode: string
  subFamilyCode: string
  supplierCode: string
  itemCode: string
  search: string
  supplierItemCode: string
  barcode: string
}

const EMPTY_FILTERS: ItemFilters = {
  familyCode: '',
  subFamilyCode: '',
  supplierCode: '',
  itemCode: '',
  search: '',
  supplierItemCode: '',
  barcode: '',
}

/** Almeno un criterio valorizzato: senza, la ricerca restituirebbe l'intero catalogo. */
function hasAnyCriteria(filters: ItemFilters) {
  return Object.values(filters).some((v) => v.trim() !== '')
}

/**
 * Creazione guidata: prima il punto vendita, poi l'articolo cercato fra quelli ordinabili in quel
 * punto vendita, poi i dati d'acquisto. Se l'articolo non è ordinabile l'invio è bloccato e il
 * motivo è quello che dice il gestionale, non una nostra ipotesi.
 *
 * La ricerca parte da ciò che si ha in mano: il codice a barre letto sul prodotto, il codice sul
 * cartellino, il codice del catalogo fornitore, oppure la famiglia dello scaffale. Per questo la
 * ricerca è esplicita e non a ogni tasto: con più criteri combinati, interrogare il gestionale
 * mentre si scrive produrrebbe richieste su filtri incompleti.
 */
function NewSpecialOrder({
  stores,
  defaultStore,
  onCreated,
}: {
  stores: { code: string; name: string }[]
  defaultStore: string
  /** `summedFrom` valorizzato = la quantità è stata sommata a quei pezzi già in sospeso. */
  onCreated: (order: SpecialOrder, requestedQuantity: number, summedFrom?: number) => void
}) {
  const [storeCode, setStoreCode] = useState(defaultStore)

  const [filters, setFilters] = useState<ItemFilters>(EMPTY_FILTERS)
  const [results, setResults] = useState<Item[]>()
  const [searching, setSearching] = useState(false)
  const [searchError, setSearchError] = useState<unknown>()
  /** Vero dopo la prima ricerca eseguita: prima non ha senso dire "nessun risultato". */
  const [searched, setSearched] = useState(false)
  /** Solo l'ultima ricerca avviata può scrivere il risultato a video. */
  const requestId = useRef(0)

  const [families, setFamilies] = useState<ItemFamily[]>()
  const [familiesError, setFamiliesError] = useState<unknown>()
  const [subFamilies, setSubFamilies] = useState<ItemSubFamily[]>()
  const [subFamiliesLoading, setSubFamiliesLoading] = useState(false)
  const [subFamiliesError, setSubFamiliesError] = useState<unknown>()

  const [picked, setPicked] = useState<Item>()
  const [purchase, setPurchase] = useState<ItemPurchaseInfo | null>()
  const [purchaseLoading, setPurchaseLoading] = useState(false)
  const [purchaseError, setPurchaseError] = useState<unknown>()

  /** Giacenze e pezzi già richiesti, per articolo: arrivano dopo i risultati, senza bloccarli. */
  const [availability, setAvailability] = useState<Map<string, SpecialOrderAvailability>>()

  /** Pezzi già in sospeso per l'articolo scelto, riletti al momento della scelta. */
  const [pickedPending, setPickedPending] = useState<number>()
  /** Con una richiesta già in sospeso: sommare (default) o sostituire. */
  const [applyMode, setApplyMode] = useState<'Add' | 'Replace'>('Add')

  const [quantity, setQuantity] = useState('1')
  const [submitting, setSubmitting] = useState(false)
  const [problem, setProblem] = useState<string>()
  const [failure, setFailure] = useState<unknown>()

  const criteriaSet = hasAnyCriteria(filters)

  // Famiglie merceologiche: elenco chiuso e breve, si legge una volta sola all'apertura.
  useEffect(() => {
    let cancelled = false

    api
      .get<ItemFamily[]>('/item-families')
      .then((data) => {
        if (!cancelled) setFamilies(data)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setFamiliesError(e)
        setFamilies(undefined)
      })

    return () => {
      cancelled = true
    }
  }, [])

  // Sottofamiglie: dipendono dalla famiglia scelta, perché il loro codice non è univoco da solo.
  useEffect(() => {
    if (!filters.familyCode) {
      setSubFamilies(undefined)
      setSubFamiliesError(undefined)
      return
    }

    let cancelled = false
    setSubFamiliesLoading(true)
    setSubFamiliesError(undefined)

    api
      .get<ItemSubFamily[]>('/item-subfamilies', { familyCode: filters.familyCode })
      .then((data) => {
        if (!cancelled) setSubFamilies(data)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setSubFamiliesError(e)
        setSubFamilies(undefined)
      })
      .finally(() => {
        if (!cancelled) setSubFamiliesLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [filters.familyCode])

  const setFilter = (patch: Partial<ItemFilters>) => setFilters((current) => ({ ...current, ...patch }))

  const resetFilters = () => {
    setFilters(EMPTY_FILTERS)
    setResults(undefined)
    setAvailability(undefined)
    setSearchError(undefined)
    setSearched(false)
    setPicked(undefined)
  }

  const runSearch = async () => {
    if (!storeCode || !criteriaSet) return

    const id = ++requestId.current
    setSearching(true)
    setSearchError(undefined)
    setSearched(true)
    setPicked(undefined)

    // I criteri vuoti non vengono spediti: il client li omette dalla query string.
    const trimmed = (v: string) => v.trim() || undefined

    try {
      const data = await api.get<Item[]>('/items', {
        search: trimmed(filters.search),
        itemCode: trimmed(filters.itemCode),
        supplierCode: trimmed(filters.supplierCode),
        supplierItemCode: trimmed(filters.supplierItemCode),
        barcode: trimmed(filters.barcode),
        familyCode: trimmed(filters.familyCode),
        subFamilyCode: trimmed(filters.subFamilyCode),
        orderableInStore: storeCode,
        take: 50,
      })

      if (id === requestId.current) setResults(data)

      // Le giacenze arrivano in un secondo giro: se falliscono la griglia resta senza numeri,
      // non è un motivo per negare la ricerca.
      setAvailability(undefined)
      if (data.length > 0) {
        void api
          .get<SpecialOrderAvailability[]>('/special-orders/availability', {
            storeCode,
            itemCode: data.map((r) => r.code),
          })
          .then((rows) => {
            if (id === requestId.current)
              setAvailability(new Map(rows.map((r) => [r.item, r])))
          })
          .catch(() => undefined)
      }
    } catch (e) {
      if (id !== requestId.current) return
      setSearchError(e)
      setResults(undefined)
    } finally {
      if (id === requestId.current) setSearching(false)
    }
  }

  /** Invio da un qualsiasi campo avvia la ricerca: è il gesto del lettore di codici a barre. */
  const onFieldKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      void runSearch()
    }
  }

  // Dati d'acquisto dell'articolo scelto, per quel punto vendita. Insieme si rilegge il già
  // richiesto: la mappa della griglia può essere vecchia di qualche minuto, e qui la freschezza
  // decide se proporre la scelta somma/sostituisci.
  useEffect(() => {
    if (!picked || !storeCode) {
      setPurchase(undefined)
      setPickedPending(undefined)
      return
    }

    let cancelled = false
    setPurchaseLoading(true)
    setPurchaseError(undefined)
    setPickedPending(undefined)
    setApplyMode('Add')

    api
      .get<ItemPurchaseInfo[]>(`/stores/${encodeURIComponent(storeCode)}/items/purchase-info`, {
        itemCode: picked.code,
      })
      .then((data) => {
        if (!cancelled) setPurchase(data[0] ?? null)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setPurchaseError(e)
        setPurchase(undefined)
      })
      .finally(() => {
        if (!cancelled) setPurchaseLoading(false)
      })

    void api
      .get<SpecialOrderAvailability[]>('/special-orders/availability', {
        storeCode,
        itemCode: picked.code,
      })
      .then((rows) => {
        if (!cancelled) setPickedPending(rows[0]?.pendingQuantity ?? 0)
      })
      .catch(() => undefined)

    return () => {
      cancelled = true
    }
  }, [picked, storeCode])

  const packSize = purchase?.packSize ?? 1
  const requested = Number(quantity)
  const validQuantity = Number.isFinite(requested) && Math.trunc(requested) >= 1
  const rounded = validQuantity ? toNearestPack(Math.trunc(requested), packSize) : 0
  const orderable = purchase === null || purchase?.isOrderable !== false

  // La griglia dei risultati: le colonne di giacenza leggono la mappa, quindi si ridefiniscono
  // quando arriva (o cambia) la disponibilità.
  const resultColumns = useMemo<ColumnDef<Item>[]>(
    () => [
      { key: 'code', label: 'Codice', value: (r) => r.code },
      { key: 'description', label: 'Descrizione', value: (r) => r.description },
      {
        key: 'supplier',
        label: 'Fornitore',
        value: (r) => `${r.supplierName ?? ''} ${r.supplier}`.trim(),
      },
      {
        key: 'family',
        label: 'Famiglia',
        value: (r) =>
          `${r.familyName ?? r.familyCode ?? ''}${
            r.subFamilyName || r.subFamilyCode ? ` › ${r.subFamilyName ?? r.subFamilyCode}` : ''
          }`,
      },
      { key: 'barcode', label: 'EAN', value: (r) => r.barcode ?? '' },
      {
        key: 'storeStock',
        label: 'Giacenza PDV',
        value: (r) => availability?.get(r.code)?.storeStock ?? null,
        numeric: true,
      },
      {
        key: 'cediStock',
        label: 'Giacenza CEDI',
        value: (r) => availability?.get(r.code)?.cediStock ?? null,
        numeric: true,
        title: 'Vuota per gli articoli a fornitore diretto: non passano da un CEDI.',
      },
      {
        key: 'pending',
        label: 'Già richiesto',
        value: (r) => availability?.get(r.code)?.pendingQuantity ?? null,
        numeric: true,
        title: 'Pezzi già in ordini speciali in sospeso per questo punto vendita.',
      },
    ],
    [availability],
  )
  const resultsGrid = useGrid(results ?? [], resultColumns)

  const canSubmit =
    !!storeCode && !!picked && validQuantity && orderable && !purchaseLoading && !submitting

  const submit = async () => {
    if (!picked || !canSubmit) return

    setSubmitting(true)
    setProblem(undefined)
    setFailure(undefined)

    const wanted = Math.trunc(requested)
    const summing = (pickedPending ?? 0) > 0 && applyMode === 'Add'

    try {
      const created = await api.post<SpecialOrder>('/special-orders', {
        storeCode,
        itemCode: picked.code,
        quantity: wanted,
        mode: summing ? 'Add' : 'Replace',
      })

      resetFilters()
      setQuantity('1')
      onCreated(created, wanted, summing ? pickedPending : undefined)
    } catch (e) {
      // Un 422 qui è una regola di business (articolo non ordinabile, tetto di valore superato):
      // va letto accanto al pulsante, non come guasto della pagina.
      if (e instanceof ApiError && (e.kind === 'validation' || e.kind === 'forbidden')) {
        setProblem(e.detail || e.title)
      } else {
        setFailure(e)
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Card title="Nuovo ordine speciale">
      <div className="grid grid-2">
        <div>
          <Field label="Punto vendita">
            <select
              value={storeCode}
              onChange={(e) => {
                // I risultati valgono per un punto vendita solo: cambiandolo vanno rifatti.
                setStoreCode(e.target.value)
                setPicked(undefined)
                setResults(undefined)
                setAvailability(undefined)
                setSearched(false)
              }}
            >
              <option value="">— scegli —</option>
              {stores.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.code} — {s.name}
                </option>
              ))}
            </select>
          </Field>

          {/* Il codice a barre è il primo campo e prende il fuoco: chi ha il lettore in mano spara
              il codice e preme Invio senza toccare il mouse. */}
          <Field label="Codice a barre" hint="Ricerca esatta: leggi il codice e premi Invio.">
            <input
              autoFocus
              placeholder="Lettore o digitazione"
              value={filters.barcode}
              disabled={!storeCode}
              onChange={(e) => setFilter({ barcode: e.target.value })}
              onKeyDown={onFieldKeyDown}
            />
          </Field>

          <div className="grid grid-2 filter-grid">
            <Field label="Famiglia">
              <select
                value={filters.familyCode}
                disabled={!storeCode || !families || families.length === 0}
                onChange={(e) =>
                  // Cambiando famiglia la sottofamiglia precedente non ha più senso: si azzera.
                  setFilter({ familyCode: e.target.value, subFamilyCode: '' })
                }
              >
                <option value="">
                  {families === undefined && familiesError === undefined ?
                    'Caricamento…'
                  : 'Tutte le famiglie'}
                </option>
                {families?.map((f) => (
                  <option key={f.code} value={f.code}>
                    {f.code} — {f.description}
                  </option>
                ))}
              </select>
            </Field>

            <Field
              label="Sottofamiglia"
              hint={filters.familyCode ? undefined : 'Scegli prima la famiglia.'}
            >
              <select
                value={filters.subFamilyCode}
                disabled={!storeCode || !filters.familyCode || subFamiliesLoading || !subFamilies}
                onChange={(e) => setFilter({ subFamilyCode: e.target.value })}
              >
                <option value="">
                  {subFamiliesLoading ? 'Caricamento…' : 'Tutte le sottofamiglie'}
                </option>
                {subFamilies?.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.code} — {s.description}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Codice fornitore" hint="Il codice usato dal fornitore nel suo catalogo.">
              <input
                value={filters.supplierItemCode}
                disabled={!storeCode}
                onChange={(e) => setFilter({ supplierItemCode: e.target.value })}
                onKeyDown={onFieldKeyDown}
              />
            </Field>

            <Field label="Fornitore" hint="Cerca per nome o codice.">
              <SupplierPicker
                value={filters.supplierCode}
                disabled={!storeCode}
                onChange={(code) => setFilter({ supplierCode: code })}
              />
            </Field>

            <Field label="Codice interno">
              <input
                value={filters.itemCode}
                disabled={!storeCode}
                onChange={(e) => setFilter({ itemCode: e.target.value })}
                onKeyDown={onFieldKeyDown}
              />
            </Field>

            <Field label="Descrizione">
              <input
                value={filters.search}
                disabled={!storeCode}
                onChange={(e) => setFilter({ search: e.target.value })}
                onKeyDown={onFieldKeyDown}
              />
            </Field>
          </div>

          {familiesError !== undefined && <ErrorBox error={familiesError} />}
          {subFamiliesError !== undefined && <ErrorBox error={subFamiliesError} />}

          {families && families.length === 0 && (
            <Empty>Nessuna famiglia merceologica in anagrafica: usa gli altri criteri.</Empty>
          )}

          {filters.familyCode && !subFamiliesLoading && subFamilies && subFamilies.length === 0 && (
            <Empty>La famiglia scelta non ha sottofamiglie: la ricerca userà la sola famiglia.</Empty>
          )}

          <div className="inline" style={{ marginBottom: 12 }}>
            <Button
              variant="primary"
              disabled={!storeCode || !criteriaSet || searching}
              onClick={() => void runSearch()}
            >
              {searching ? 'Ricerca…' : 'Cerca'}
            </Button>
            <Button variant="ghost" disabled={searching} onClick={resetFilters}>
              Azzera filtri
            </Button>
          </div>

          {!storeCode && <Empty>Scegli prima il punto vendita: l’ordinabilità dipende da quello.</Empty>}

          {storeCode && !criteriaSet && (
            <Empty>
              Indica almeno un criterio — codice a barre, famiglia, fornitore, codice interno,
              codice fornitore o descrizione — e premi “Cerca”. Senza criteri la ricerca
              restituirebbe l’intero catalogo.
            </Empty>
          )}
        </div>

        <div>
          {!picked && <Empty>Scegli un articolo per vedere i dati d’acquisto.</Empty>}

          {picked && (
            <>
              <div className="inline" style={{ marginBottom: 12, justifyContent: 'space-between' }}>
                <div>
                  <strong>{picked.description}</strong>
                  <div className="muted num">{picked.code}</div>
                </div>
                <Button variant="ghost" onClick={() => setPicked(undefined)}>
                  Cambia articolo
                </Button>
              </div>

              {purchaseLoading && <Spinner label="Lettura dei dati d’acquisto…" />}
              {!purchaseLoading && purchaseError !== undefined && <ErrorBox error={purchaseError} />}

              {!purchaseLoading && purchase === null && (
                <div className="notice notice-warn">
                  <strong>Dati d’acquisto non disponibili</strong>
                  <p>
                    Il gestionale non espone la confezione per questo articolo in questo punto
                    vendita. La quantità verrà registrata a pezzi singoli.
                  </p>
                </div>
              )}

              {!purchaseLoading && purchase && (
                <>
                  <div className="grid grid-2" style={{ marginBottom: 12 }}>
                    <div className="stat">
                      <div className="stat-label">Confezione</div>
                      <div className="stat-value num">{purchase.packSize}</div>
                      <div className="stat-note">
                        {purchase.sourceLocation ?
                          `pezzi per collo · da ${purchase.sourceLocation}`
                        : 'pezzi per collo'}
                      </div>
                    </div>
                    <div className="stat">
                      <div className="stat-label">Ordinabilità</div>
                      <div className="stat-value">
                        <Badge tone={purchase.isOrderable ? 'ok' : 'danger'}>
                          {purchase.isOrderable ? 'Ordinabile' : 'Non ordinabile'}
                        </Badge>
                      </div>
                      <div className="stat-note">nel punto vendita {storeCode}</div>
                    </div>
                    <div className="stat">
                      <div className="stat-label">Già richiesto</div>
                      <div className="stat-value num">{pickedPending ?? '—'}</div>
                      <div className="stat-note">pezzi in ordini speciali in sospeso</div>
                    </div>
                  </div>

                  {!purchase.isOrderable && (
                    <div className="notice notice-error">
                      <strong>Articolo non ordinabile in questo punto vendita</strong>
                      <p>{purchase.notOrderableReason ?? 'Il gestionale non ne indica il motivo.'}</p>
                    </div>
                  )}
                </>
              )}

              <Field
                label="Quantità richiesta"
                hint={
                  packSize > 1 ?
                    `Verrà arrotondata al multiplo di ${packSize} più vicino, con almeno una confezione.`
                  : 'Articolo a pezzo singolo: nessun arrotondamento.'
                }
              >
                <input
                  className="qty-input"
                  inputMode="numeric"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                />
              </Field>

              {(pickedPending ?? 0) > 0 && (
                <div className="notice notice-info">
                  <strong>
                    Per questo articolo hai già {plural(pickedPending ?? 0, 'pezzo richiesto', 'pezzi richiesti')} in sospeso.
                  </strong>
                  <div style={{ marginTop: 6 }}>
                    <label className="radio">
                      <input
                        type="radio"
                        checked={applyMode === 'Add'}
                        onChange={() => setApplyMode('Add')}
                      />
                      Somma alla richiesta esistente
                    </label>
                    <label className="radio">
                      <input
                        type="radio"
                        checked={applyMode === 'Replace'}
                        onChange={() => setApplyMode('Replace')}
                      />
                      Sostituisci la richiesta esistente
                    </label>
                  </div>
                </div>
              )}

              {validQuantity && (
                <p className={rounded === Math.trunc(requested) ? 'muted' : 'notice notice-info'}>
                  {(pickedPending ?? 0) > 0 ?
                    applyMode === 'Add' ?
                      `La richiesta passa da ${pickedPending} a ${(pickedPending ?? 0) + rounded} pezzi (${rounded} in più${rounded === Math.trunc(requested) ? '' : `, arrotondati da ${Math.trunc(requested)} al multiplo di ${packSize}`}).`
                    : `La richiesta passa da ${pickedPending} a ${rounded} pezzi${rounded === Math.trunc(requested) ? '' : ` (arrotondati da ${Math.trunc(requested)} al multiplo di ${packSize})`}.`
                  : rounded === Math.trunc(requested) ?
                    `Verranno registrati ${rounded} pezzi.`
                  : `Richiesti ${Math.trunc(requested)} pezzi: ne verranno registrati ${rounded} (multiplo di ${packSize}).`}
                </p>
              )}

              {!validQuantity && <p className="cell-problem">La quantità deve essere almeno 1.</p>}

              {problem && (
                <div className="notice notice-warn">
                  <strong>Richiesta non accettata</strong>
                  <p>{problem}</p>
                </div>
              )}

              {failure !== undefined && <ErrorBox error={failure} onRetry={() => void submit()} />}

              <div className="inline">
                <Button variant="primary" disabled={!canSubmit} onClick={() => void submit()}>
                  {submitting ? 'Invio…' : 'Crea ordine speciale'}
                </Button>
                {!orderable && <span className="muted">Invio bloccato: l’articolo non è ordinabile.</span>}
              </div>
            </>
          )}
        </div>
      </div>

      {/* I risultati stanno sotto, a tutta larghezza: con giacenze e già richiesto le colonne
          sono tante, e in mezza pagina non ci starebbero. */}
      {searching && <Spinner label="Ricerca articoli…" />}
      {!searching && searchError !== undefined && <ErrorBox error={searchError} onRetry={() => void runSearch()} />}

      {!searching && searchError === undefined && searched && results && results.length === 0 && (
        <Empty>
          Nessun articolo ordinabile in questo punto vendita corrisponde ai criteri indicati.
          Togli qualche filtro o controlla il codice a barre.
        </Empty>
      )}

      {!searching && results && results.length > 0 && !picked && (
        <div className="table-wrap">
          <table className="dense">
            <GridHead columns={resultColumns} grid={resultsGrid} extraColumns={1} />
            <tbody>
              {resultsGrid.rows.map((item) => {
                const info = availability?.get(item.code)
                return (
                  <tr key={item.code} className="row-pick" onClick={() => setPicked(item)}>
                    <td className="num">{item.code}</td>
                    <td>
                      {item.description}
                      {item.isCommerciallyBlocked && (
                        <div className="muted">bloccato commercialmente</div>
                      )}
                      {item.supplierItemCode && (
                        <div className="muted">cod. fornitore {item.supplierItemCode}</div>
                      )}
                    </td>
                    <td>
                      {item.supplierName ?? `fornitore ${item.supplier}`}{' '}
                      <span className="muted">({item.supplier})</span>
                    </td>
                    <td>
                      {item.familyName ?? item.familyCode ?? <span className="muted">—</span>}
                      {item.subFamilyName || item.subFamilyCode ?
                        ` › ${item.subFamilyName ?? item.subFamilyCode}`
                      : ''}
                    </td>
                    <td className="num">{item.barcode ?? <span className="muted">—</span>}</td>
                    <td className="num">
                      {info?.storeStock === undefined ?
                        <span className="muted">—</span>
                      : <Num value={info.storeStock} />}
                    </td>
                    <td className="num">
                      {info?.cediStock === undefined || info?.cediStock === null ?
                        <span className="muted">—</span>
                      : <Num value={info.cediStock} />}
                    </td>
                    <td className="num">
                      {info === undefined ?
                        <span className="muted">—</span>
                      : info.pendingQuantity > 0 ?
                        <strong><Num value={info.pendingQuantity} /></strong>
                      : <span className="muted">0</span>}
                    </td>
                    <td>
                      {/* Fa lo stesso del click sulla riga: esiste perché si veda che si può. */}
                      <Button variant="ghost" onClick={() => setPicked(item)}>
                        Scegli
                      </Button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          <GridSummary grid={resultsGrid} total={results.length} />
          <div className="muted" style={{ padding: '6px 4px' }}>
            Vengono mostrati al massimo 50 risultati: restringi la ricerca se non trovi l’articolo.
          </div>
        </div>
      )}
    </Card>
  )
}

// --- Campagne centralizzate ---------------------------------------------------------------------

/**
 * Campagne di sede in sola consultazione: in questa versione si guardano e basta, la creazione e
 * l'applicazione restano fuori. Le righe si caricano su richiesta, una campagna alla volta.
 */
function Campaigns() {
  const [campaigns, setCampaigns] = useState<CentralCampaign[]>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<unknown>()
  const [reloadToken, setReloadToken] = useState(0)

  const [openId, setOpenId] = useState<number>()
  const [creating, setCreating] = useState(false)
  const [name, setName] = useState('')
  const [supplier, setSupplier] = useState<string>()
  const [mode, setMode] = useState<'Replace' | 'Add'>('Replace')
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [createError, setCreateError] = useState<string>()

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(undefined)

    api
      .get<CentralCampaign[]>('/special-orders/campaigns')
      .then((data) => {
        if (!cancelled) setCampaigns(data)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setError(e)
        setCampaigns(undefined)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [reloadToken])

  const reload = () => setReloadToken((t) => t + 1)

  const create = async () => {
    setCreateError(undefined)
    if (name.trim().length < 3) {
      setCreateError('Il nome della campagna serve a ritrovarla: almeno tre caratteri.')
      return
    }

    setSaving(true)
    try {
      const created = await api.post<CentralCampaign>('/special-orders/campaigns', {
        name: name.trim(),
        supplierCode: supplier,
        mode,
        note: note.trim() || undefined,
      })
      setCreating(false)
      setName(''); setSupplier(undefined); setNote(''); setMode('Replace')
      reload()
      // Si apre subito in composizione: una campagna vuota non serve a nessuno.
      setOpenId(created.id)
    } catch (e: unknown) {
      setCreateError(e instanceof ApiError ? (e.detail ?? e.title) : 'Creazione non riuscita.')
    } finally {
      setSaving(false)
    }
  }


  return (
    <Card
      title="Campagne centralizzate"
      actions={
        <div className="card-actions">
          <Button onClick={reload}>Aggiorna</Button>
          <Button variant="primary" onClick={() => setCreating((c) => !c)}>
            {creating ? 'Annulla' : '+ Nuova campagna'}
          </Button>
        </div>
      }
    >
      {creating && (
        <div className="campaign-new">
          <div className="grid filter-grid">
            <Field label="Nome della campagna" hint="Come la ritroverai in elenco.">
              <input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
            </Field>

            {/* Facoltativo: senza, ogni ordine creato dall'applicazione prende il fornitore
                del proprio articolo, come nella creazione singola. Indicarlo restringe la
                ricerca articoli in composizione. */}
            <Field
              label="Fornitore"
              hint="Facoltativo: senza, ogni articolo prende il suo. Indicarlo restringe la ricerca."
            >
              <SupplierPicker
                value={supplier ?? ''}
                onChange={(code) => setSupplier(code || undefined)}
              />
            </Field>

            <Field
              label="Modalità"
              hint={
                mode === 'Replace' ?
                  'Sostituisce le quantità già richieste; zero annulla la richiesta.'
                : 'Somma alle quantità già richieste.'
              }
            >
              <select value={mode} onChange={(e) => setMode(e.target.value as 'Replace' | 'Add')}>
                <option value="Replace">Sostituisci</option>
                <option value="Add">Somma</option>
              </select>
            </Field>
          </div>

          <Field label="Nota" hint="Facoltativa.">
            <input value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>

          {createError && <div className="notice notice-error">{createError}</div>}

          <Button variant="primary" onClick={() => void create()} disabled={saving}>
            {saving ? 'Creazione…' : 'Crea e componi'}
          </Button>
        </div>
      )}

      {loading && <Spinner />}
      {!loading && error !== undefined && <ErrorBox error={error} onRetry={() => setReloadToken((t) => t + 1)} />}

      {!loading && error === undefined && campaigns && campaigns.length === 0 && (
        <Empty>
          Nessuna campagna centralizzata registrata. Le campagne le prepara la sede: finché non ne
          viene creata una, questo elenco resta vuoto.
        </Empty>
      )}

      {!loading && error === undefined && campaigns && campaigns.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Campagna</th>
                <th>Fornitore</th>
                <th>Modalità</th>
                <th>Stato</th>
                <th className="num">Righe</th>
                <th>Creata da</th>
                <th>Creata il</th>
                <th>Applicata il</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {campaigns.map((campaign) => (
                <tr key={campaign.id}>
                  <td>
                    {campaign.name}
                    {campaign.note && <div className="muted">{campaign.note}</div>}
                  </td>
                  <td className="num">{campaign.supplier ?? '—'}</td>
                  <td title={campaign.mode === 'Replace' ? 'Sostituisce le quantità' : 'Somma alle quantità esistenti'}>
                    {campaign.mode === 'Replace' ? 'Sostituisce' : 'Somma'}
                  </td>
                  <td>
                    <Badge
                      tone={
                        campaign.status === 'Applied' ? 'ok'
                        : campaign.status === 'Cancelled' ? 'muted'
                        : 'warn'
                      }
                    >
                      {campaign.status === 'Applied' ?
                        'Applicata'
                      : campaign.status === 'Cancelled' ?
                        'Annullata'
                      : 'Bozza'}
                    </Badge>
                  </td>
                  <td className="num">
                    <Num value={campaign.lineCount} />
                  </td>
                  <td>{campaign.createdBy}</td>
                  <td>{formatDateTime(campaign.createdAt)}</td>
                  <td>{campaign.appliedAt ? formatDate(campaign.appliedAt) : '—'}</td>
                  <td>
                    <Button
                      variant="ghost"
                      onClick={() => setOpenId((current) => (current === campaign.id ? undefined : campaign.id))}
                    >
                      {openId === campaign.id ? 'Chiudi' : campaign.status === 'Draft' ? 'Componi' : 'Vedi righe'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openId !== undefined && campaigns && (
        <div style={{ marginTop: 16 }}>
          {(() => {
            const campaign = campaigns.find((c) => c.id === openId)
            return campaign ? (
              <CampaignEditor
                campaign={campaign}
                onChanged={reload}
                onClose={() => setOpenId(undefined)}
              />
            ) : null
          })()}
        </div>
      )}

    </Card>
  )
}
